import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BackButton from "../../components/common/BackButton";
import Loader from "../../components/common/Loader";
import { SERVER_ORIGIN } from "../../utilities/constants";
import { hasAdminAccess, getDashboardPath } from "../../utilities/helper_functions";
import { toast } from "react-hot-toast";
import css from "../../css/admin/users-page.module.css";

const getAdminAuthHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
    "auth-token": sessionStorage.getItem("token"),
  };
  const adminId = process.env.REACT_APP_ADMIN_ID || "yuva@gmail.com";
  const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || "admin@yuva123";
  headers.Authorization = `Basic ${btoa(`${adminId}:${adminPassword}`)}`;
  return headers;
};


const LABELS = {
  student_upload_reminder_days: "Student upload reminder interval (days)",
  student_upload_year_completion_reminder_days: "Days before 1-year completion to send reminder",
  student_upload_year_duration_years: "Years after last upload for completion reminder",
  mou_duration_default_years: "Default MoU duration (years)",
  tc_reminder_days_before_expiry: "T&C reminder days before expiry",
  yuva_ec_reminder_days_before_expiry: "YUVA EC reminder days before expiry",
  email_template_student_upload_reminder_slug: "Email template slug (student upload reminder)",
  email_template_one_year_completion_reminder_slug: "Email template slug (1-year completion reminder)",
};

const ConfigurationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({});
  const [list, setList] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const hasAdmin = hasAdminAccess();

  // Schedule Email state
  const [scheduleRecurrence, setScheduleRecurrence] = useState("once");
  const [scheduleDate, setScheduleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [scheduleTime, setScheduleTime] = useState("14:00");
  const [scheduleSubject, setScheduleSubject] = useState("");
  const [scheduleMessage, setScheduleMessage] = useState("");
  const [scheduleEmails, setScheduleEmails] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);

  useEffect(() => {
    if (!hasAdmin) {
      navigate("/admin/services");
      return;
    }
    fetchConfig();
  }, [hasAdmin, navigate]);

  useEffect(() => {
    if (hasAdmin) fetchScheduledEmails();
  }, [hasAdmin]);

  const fetchScheduledEmails = async () => {
    setLoadingScheduled(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/scheduled-email`, {
        method: "GET",
        headers: getAdminAuthHeaders(),
      });
      const result = await res.json();
      if (res.ok && result.data) setScheduledList(result.data);
    } catch (err) {
      console.error("Scheduled emails fetch error:", err);
    }
    setLoadingScheduled(false);
  };

  const parseRecipientEmails = (text) => {
    if (!text || !String(text).trim()) return [];
    const raw = String(text)
      .split(/[\n,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && s.includes("@"));
    const seen = new Set();
    return raw.filter((e) => {
      if (seen.has(e)) return false;
      seen.add(e);
      return true;
    });
  };
  const handleScheduleEmail = async () => {
    if (!scheduleSubject.trim() || !scheduleMessage.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    const recipients = parseRecipientEmails(scheduleEmails);
    if (recipients.length === 0) {
      toast.error("Add at least one recipient email (one per line or comma-separated)");
      return;
    }
    let runAt = null;
    // if (scheduleRecurrence === "once") {
    //   if (!scheduleDate || !scheduleTime) {
    //     toast.error("Select date and time for one-time schedule");
    //     return;
    //   }

    //   // Create a date object with the selected date/time
    //   const selectedDateTime = new Date(`${scheduleDate}T${scheduleTime}:00`);

    //   // Store as ISO string (this will be in local timezone)
    //   runAt = selectedDateTime.toISOString();

    //   // Validate that it's in the future
    //   if (selectedDateTime <= new Date()) {
    //     toast.error("Schedule date/time must be in the future");
    //     return;
    //   }
    // } 
    if (scheduleRecurrence === "once") {
      if (!scheduleDate || !scheduleTime) {
        toast.error("Select date and time for one-time schedule");
        return;
      }

      // Create LOCAL date (IST)
      const localDate = new Date(`${scheduleDate}T${scheduleTime}:00`);

      // Store as UTC instant corresponding to the chosen local time
      runAt = localDate.toISOString();

      // Validate future date
      if (localDate <= new Date()) {
        toast.error("Schedule date/time must be in the future");
        return;
      }
    }
    else {
      const [h, m] = scheduleTime.split(":").map(Number);

      const now = new Date();
      const localNext = new Date();
      localNext.setHours(h || 0, m || 0, 0, 0);

      // If time passed today → next day
      if (localNext <= now) {
        localNext.setDate(localNext.getDate() + 1);
      }

      const utcNext = new Date(
        localNext.getTime() - localNext.getTimezoneOffset() * 60000
      );

      runAt = utcNext.toISOString();
    }

    // else {
    //   const [h, m] = scheduleTime.split(":").map(Number);
    //   const next = new Date();
    //   next.setHours(h || 0, m || 0, 0, 0);
    //   if (next <= new Date()) next.setDate(next.getDate() + 1);
    //   runAt = next.toISOString();
    // }
    setScheduling(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/scheduled-email`, {
        method: "POST",
        headers: getAdminAuthHeaders(),
        body: JSON.stringify({
          subject: scheduleSubject.trim(),
          message: scheduleMessage.trim(),
          runAt: scheduleRecurrence === "once" ? runAt : undefined,
          recurrence: scheduleRecurrence,
          timeOfDay: scheduleRecurrence === "daily" ? scheduleTime : undefined,
          customRecipients: recipients.map((email) => ({
            email,
            name: "",
            userType: "custom",
          })),
        }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success("Email scheduled successfully.");
        setScheduleSubject("");
        setScheduleMessage("");
        setScheduleEmails("");
        fetchScheduledEmails();
      } else {
        toast.error(result.message || "Failed to schedule email");
      }
    } catch (err) {
      console.error("Schedule email error:", err);
      toast.error("Failed to schedule email");
    }
    setScheduling(false);
  };

  // const handleScheduleEmail = async () => {
  //   if (!scheduleSubject.trim() || !scheduleMessage.trim()) {
  //     toast.error("Subject and message are required");
  //     return;
  //   }
  //   const recipients = parseRecipientEmails(scheduleEmails);
  //   if (recipients.length === 0) {
  //     toast.error("Add at least one recipient email (one per line or comma-separated)");
  //     return;
  //   }
  //   let runAt = null;
  //   if (scheduleRecurrence === "once") {
  //     if (!scheduleDate || !scheduleTime) {
  //       toast.error("Select date and time for one-time schedule");
  //       return;
  //     }
  //     const local = new Date(`${scheduleDate}T${scheduleTime}:00`);
  //     runAt = new Date(local.getTime() - local.getTimezoneOffset() * 60000).toISOString();

  //     // runAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
  //     if (new Date(runAt) <= new Date()) {
  //       toast.error("Schedule date/time must be in the future");
  //       return;
  //     }
  //   } else {
  //     const [h, m] = scheduleTime.split(":").map(Number);
  //     const next = new Date();
  //     next.setHours(h || 0, m || 0, 0, 0);
  //     if (next <= new Date()) next.setDate(next.getDate() + 1);
  //     runAt = next.toISOString();
  //   }
  //   setScheduling(true);
  //   try {
  //     const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/scheduled-email`, {
  //       method: "POST",
  //       headers: getAdminAuthHeaders(),
  //       body: JSON.stringify({
  //         subject: scheduleSubject.trim(),
  //         message: scheduleMessage.trim(),
  //         runAt: scheduleRecurrence === "once" ? runAt : undefined,
  //         recurrence: scheduleRecurrence,
  //         timeOfDay: scheduleRecurrence === "daily" ? scheduleTime : undefined,
  //         customRecipients: recipients.map((email) => ({
  //           email,
  //           name: "",
  //           userType: "custom",
  //         })),



  //         // customRecipients: recipients.map((email) => ({ email, name: "" })),
  //       }),
  //     });
  //     const result = await res.json();
  //     if (res.ok) {
  //       toast.success("Email scheduled successfully.");
  //       setScheduleSubject("");
  //       setScheduleMessage("");
  //       setScheduleEmails("");
  //       fetchScheduledEmails();
  //     } else {
  //       toast.error(result.message || "Failed to schedule email");
  //     }
  //   } catch (err) {
  //     console.error("Schedule email error:", err);
  //     toast.error("Failed to schedule email");
  //   }
  //   setScheduling(false);
  // };
  useEffect(() => {
    if (!hasAdmin) return;

    const fetchAndSchedule = () => {
      fetchScheduledEmails();
    };

    fetchAndSchedule(); // immediate first fetch

    const intervalId = setInterval(fetchAndSchedule, 20_000); // 10 seconds

    return () => clearInterval(intervalId);
  }, [hasAdmin]);

  const handleCancelScheduled = async (id) => {
    try {
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/scheduled-email/${id}`, {
        method: "DELETE",
        headers: getAdminAuthHeaders(),
      });
      if (res.ok) {
        toast.success("Scheduled email cancelled.");
        fetchScheduledEmails();
      } else {
        const result = await res.json();
        toast.error(result.message || "Failed to cancel");
      }
    } catch (err) {
      toast.error("Failed to cancel scheduled email");
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/config`, {
        method: "GET",
        headers: getAdminAuthHeaders(),
      });
      const result = await res.json();
      if (res.ok && result.data) {
        setList(result.data.list || []);
        const map = {};
        (result.data.list || []).forEach((c) => {
          map[c.key] = c.value;
        });
        setConfig(map);
      }
    } catch (err) {
      console.error("Config fetch error:", err);
      setMessage({ type: "error", text: "Failed to load configuration." });
    }
    setLoading(false);
  };

  const handleChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const toSend = {};
      list.forEach((item) => {
        let val = config[item.key] ?? item.value;
        if (item.type === "number") val = Number(val);
        toSend[item.key] = val;
      });
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/config`, {
        method: "PUT",
        headers: getAdminAuthHeaders(),
        body: JSON.stringify({ config: toSend }),
      });
      const result = await res.json();
      if (res.ok) {
        setMessage({ type: "success", text: "Configuration saved successfully." });
        fetchConfig();
      } else {
        setMessage({ type: "error", text: result.statusText || "Failed to save." });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to save configuration." });
    }
    setSaving(false);
  };

  const handleSeed = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/config/seed`, {
        method: "POST",
        headers: getAdminAuthHeaders(),
      });
      const result = await res.json();
      if (res.ok && result.data?.success) {
        setMessage({ type: "success", text: "Default configuration seeded." });
        fetchConfig();
      } else {
        setMessage({ type: "error", text: result.statusText || "Seed failed." });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Seed failed." });
    }
    setSaving(false);
  };

  if (!hasAdmin) return null;

  return (
    <div className={css.outerDiv}>
      <div className={css.headerSection}>
        <div className={css.headerLeft}>
          <BackButton
            text="Back to Dashboard"
            customPath={getDashboardPath()}
          />
        </div>
      </div>
      {/* <BackButton onClick={() => navigate("/admin/services")} /> */}
      {/* <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <SettingsIcon /> Centralized Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage durations, automated reminder intervals, and email template references.
          </Typography>
        </CardContent>
      </Card> */}

      {message.text && (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {/* <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loader />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Settings</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={handleSeed} disabled={saving}>
                    Seed defaults
                  </Button>
                  <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSave} disabled={saving}>
                    Save
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {list.map((item) => (
                  <Grid item xs={12} sm={6} key={item.key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={LABELS[item.key] || item.key}
                      value={config[item.key] ?? item.value ?? ""}
                      onChange={(e) => handleChange(item.key, e.target.value)}
                      type={item.type === "number" ? "number" : "text"}
                      InputProps={
                        item.type === "number"
                          ? { inputProps: { min: 0, step: 1 }, endAdornment: item.key.includes("years") ? <InputAdornment position="end">years</InputAdornment> : item.key.includes("days") ? <InputAdornment position="end">days</InputAdornment> : null }
                          : {}
                      }
                      helperText={item.description}
                    />
                  </Grid>
                ))}
              </Grid>
              {list.length === 0 && (
                <Typography color="text.secondary">No configuration keys found. Click &quot;Seed defaults&quot; to create default keys.</Typography>
              )}
            </>
          )}
        </CardContent>
      </Card> */}

      {/* <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Email templates</Typography>
          <Typography variant="body2" color="text.secondary">
            To edit email template content, use the Mass Email / Email History section where templates can be managed by slug.
          </Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => navigate("/admin/mass-email")}>
            Go to Mass Email
          </Button>
        </CardContent>
      </Card> */}

      {/* Schedule Email */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <ScheduleSendIcon /> Schedule Email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send emails at a specific date/time (one-time) or every day at a set time. Enter recipient emails below (one per line or comma-separated).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">When to send</FormLabel>
                <RadioGroup
                  row
                  value={scheduleRecurrence}
                  onChange={(e) => setScheduleRecurrence(e.target.value)}
                >
                  <FormControlLabel value="once" control={<Radio />} label="One-time (specific date & time)" />
                  <FormControlLabel value="daily" control={<Radio />} label="Daily (every day at same time)" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {scheduleRecurrence === "once" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date"
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={scheduleRecurrence === "once" ? 6 : 12}>
              <TextField
                fullWidth
                size="small"
                label={scheduleRecurrence === "daily" ? "Time (e.g. 2:00 PM every day)" : "Time"}
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <AccessTimeIcon sx={{ mr: 1, color: "action.active" }} /> }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Recipient emails"
                placeholder="one@example.com, two@example.com or one per line"
                value={scheduleEmails}
                onChange={(e) => setScheduleEmails(e.target.value)}
                multiline
                rows={3}
                helperText="One email per line or comma/semicolon-separated"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={scheduleSubject}
                onChange={(e) => setScheduleSubject(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Message (HTML supported)"
                value={scheduleMessage}
                onChange={(e) => setScheduleMessage(e.target.value)}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={scheduling ? <CircularProgress size={20} /> : <ScheduleSendIcon />}
                onClick={handleScheduleEmail}
                disabled={scheduling}
              >
                {scheduling ? "Scheduling..." : "Schedule Email"}
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Scheduled emails</Typography>
          {loadingScheduled ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Loader />
            </Box>
          ) : (
            // <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            //   <Table size="small" stickyHeader>
            <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small" aria-label="schools table">

                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Run at</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Recurrence</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000000' }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduledList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                        No scheduled emails
                      </TableCell>
                    </TableRow>
                  ) : (
                    scheduledList.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell>{row.subject}</TableCell>
                        {/* <TableCell>
                          {row.runAt
                            ? formatDateToDDMMYYYYWithTime(row.runAt)
                            : "-"
                          } */}
                        <TableCell>
                          {row.runAt
                            ? new Date(row.runAt).toLocaleString(undefined, {
                                dateStyle: "short",
                                timeStyle: "short",
                              })
                            : "-"}
                        </TableCell>
                        <TableCell>{row.recurrence === "daily" ? "Daily" : "Once"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status}
                            color={row.status === "pending" ? "warning" : row.status === "sent" ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {row.status === "pending" && (
                            <Button size="small" color="error" onClick={() => handleCancelScheduled(row._id)}>
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigurationPage;
